import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import { Button, Card } from '@talktoast/ui'

export interface GuestSignUpModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean

  /**
   * Callback to close the modal
   */
  onClose: () => void

  /**
   * Callback when user clicks Sign Up
   */
  onSignUp: () => void

  /**
   * Callback when user clicks Log In
   */
  onLogin: () => void

  /**
   * Optional title override
   */
  title?: string

  /**
   * Optional message override
   */
  message?: string

  /**
   * Context for where the modal was triggered from
   * Used for analytics/tracking
   */
  triggerContext?: 'quick-start-skip' | 'post-meal-plan' | 'favorites' | 'calendar-export' | 'other'
}

/**
 * GuestSignUpModal Component
 *
 * Reusable modal that prompts guest users to create an account.
 * Can be triggered from multiple locations in the app flow.
 *
 * Design: Uses modal overlay with value proposition messaging
 * to encourage account creation without being pushy.
 */
export default function GuestSignUpModal({
  isOpen,
  onClose,
  onSignUp,
  onLogin,
  title = 'Save Your Progress',
  message = "You're about to start using TalkToast as a guest. Create an account to save your meal plans, preferences, and favorites.",
  triggerContext = 'other'
}: GuestSignUpModalProps) {
  if (!isOpen) return null

  // Different messaging based on trigger context
  const contextMessages = {
    'quick-start-skip': "You're about to skip setup and use smart defaults. Create an account to save your preferences and meal plans.",
    'post-meal-plan': "Great job planning your meals! Create an account to save this meal plan and access it from any device.",
    'favorites': "Save this recipe to your favorites! Create an account to build your personal recipe collection.",
    'calendar-export': "Export your meal plan to your calendar! Create an account to sync across all your devices.",
    'other': message
  }

  const displayMessage = contextMessages[triggerContext] || message

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-md animate-slide-up">
          <Card className="p-6 relative shadow-2xl">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <ICONS.actions.close className={getIconSize('md')} />
            </button>

            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-toast-gold-100 flex items-center justify-center mx-auto mb-4">
              <ICONS.user.profile className={`${getIconSize('2xl')} ${getIconColor('primary')}`} />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
              {title}
            </h2>

            {/* Message */}
            <p className="text-gray-600 text-center mb-6">
              {displayMessage}
            </p>

            {/* Benefits list */}
            <div className="mb-6 space-y-2">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold-600 flex-shrink-0`} />
                <span>Save meal plans and preferences</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold-600 flex-shrink-0`} />
                <span>Build your recipe collection</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold-600 flex-shrink-0`} />
                <span>Access from any device</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={onSignUp}
                className="bg-toast-gold-600 hover:bg-toast-gold-700"
              >
                Create Account
              </Button>
              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={onLogin}
              >
                I Already Have an Account
              </Button>
              <button
                onClick={onClose}
                className="w-full text-sm text-gray-600 hover:text-gray-900 py-2"
              >
                Continue as Guest
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Animation styles */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  )
}
